/* Build in libraries */

/* Third party libraries */

import { useState } from 'react';
import TotalApplicationsWidget from '@/components/Rover/Widgets/TotalApplicationsWidget';
import Card from '@/components/UI/Card/index.tsx';
import DoughnutChart from '@/components/UI/Charts/DoughnutChart';
/* Services */
import { transformProjectTypeChartDataSets } from '@/utils';
import { GStrategiesApprovedDashboard } from './GStrategiesApprovedDashboard';

interface IApplicationDashboardComponentProps {
  data: any;
}

interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const GStrategiesDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const [viewStatus, setViewStatus] = useState('ALL');
  const applicationsList = data?.data;
  const staticColor = ['yellow', 'blue', 'gray', 'navy', 'green'];
  const widgetsData: {
    totalApplications: number;
    triedProcess: IDoughnutChart;
    totalLiensOrViolation: IDoughnutChart;
    isPropertyIdentified: IDoughnutChart;
    ownAdditionalProperty: IDoughnutChart;
    rentOrOwned: IDoughnutChart;
    financialReadinessForProject: IDoughnutChart;
    financialReadinessGeneral: IDoughnutChart;
    understandProcess: IDoughnutChart;
    communitySupport: IDoughnutChart;
    races: IPieChart;
    futureUseOfInterestedProperty: IPieChart;
    municipalities: { status: string; count: number }[];
    totalProposedSqft: number;
    statusSummary: { [key: string]: number };
    acquisitionTypes: { [key: string]: number };
    dealTypes: { [key: string]: number };
    constructionTypes: { [key: string]: number };
  } = {
    totalApplications: applicationsList?.length,
    triedProcess: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalLiensOrViolation: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#DC3545' },
      ],
    },
    isPropertyIdentified: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#808080' },
        { data: 0, color: '#C2CFE0' },
      ],
    },

    ownAdditionalProperty: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#0FCA98' },
      ],
    },

    rentOrOwned: {
      dataLabels: ['Own', 'Rent'],
      dataSets: [
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#0FCA98' },
      ],
    },

    financialReadinessForProject: {
      dataLabels: ['One', 'Two', 'Three', 'Four', 'Five'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    financialReadinessGeneral: {
      dataLabels: ['One', 'Two', 'Three', 'Four', 'Five'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    understandProcess: {
      dataLabels: ['One', 'Two', 'Three', 'Four', 'Five'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    communitySupport: {
      dataLabels: ['One', 'Two', 'Three', 'Four', 'Five'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    races: {
      label: [
        'Black or African American',
        'White',
        'American Indian and Alaska Native',
        'Asian',
        'Native Hawaiian and Other Pacific Islander',
        'Two or more races',
      ],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#F27141' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#334D6E' },
      ],
    },
    futureUseOfInterestedProperty: {
      label: ['Green space', 'Urban Agr./Homesteading', 'Small Business', 'Housing', 'Rental property'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#808080' },
        { data: 0, color: '#334D6E' },
      ],
    },
    municipalities: [
      { status: 'Avalon', count: 0 },
      { status: 'Bellevue', count: 0 },
      { status: 'Blawnox', count: 0 },
      { status: 'Braddock Hills', count: 0 },
      { status: 'Carnegie', count: 0 },
      { status: 'Clairton', count: 0 },
      { status: 'Coraopolis', count: 0 },
      { status: 'East Pittsburgh', count: 0 },
      { status: 'Elizabeth Borough', count: 0 },
      { status: 'Forward', count: 0 },
      { status: 'Glassport', count: 0 },
      { status: 'Homestead', count: 0 },
      { status: 'McKees Rocks', count: 0 },
      { status: 'Millvale', count: 0 },
      { status: 'Mount Oliver', count: 0 },
      { status: 'North Braddock', count: 0 },
      { status: 'Pitcairn', count: 0 },
      { status: 'Rankin', count: 0 },
      { status: 'Sharpsburg', count: 0 },
      { status: 'Stowe', count: 0 },
      { status: 'Swissvale', count: 0 },
      { status: 'Turtle Creek', count: 0 },
      { status: 'Verona', count: 0 },
      { status: 'Versailles', count: 0 },
      { status: 'Wall', count: 0 },
      { status: 'Whitaker', count: 0 },
      { status: 'Whitaker and Wilmerding', count: 0 },
    ],
    totalProposedSqft: 0,
    statusSummary: {},
    acquisitionTypes: {},
    dealTypes: {},
    constructionTypes: {},
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;

    // Own additional property
    if (applicationData?.step1?.section4?.ownedProperties.length > 0) {
      widgetsData.ownAdditionalProperty.dataSets[0].data += 1;
    } else {
      widgetsData.ownAdditionalProperty.dataSets[1].data += 1;
    }

    //Tried the process
    if (applicationData?.step1?.section5?.q1 === 'yes') {
      widgetsData.triedProcess.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section5?.q1 === 'no') {
      widgetsData.triedProcess.dataSets[1].data += 1;
    }

    // Total Municipal liens and/or Code violations
    if (applicationData?.step1?.section2?.codeViolation === 'yes') {
      widgetsData.totalLiensOrViolation.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section2?.codeViolation === 'no') {
      widgetsData.totalLiensOrViolation.dataSets[1].data += 1;
    }

    // Financial Readiness for Project
    if (applicationData?.step1?.section5?.projectFinancialReadiness === '1') {
      widgetsData.financialReadinessForProject.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section5?.projectFinancialReadiness === '2') {
      widgetsData.financialReadinessForProject.dataSets[1].data += 1;
    }
    if (applicationData?.step1?.section5?.projectFinancialReadiness === '3') {
      widgetsData.financialReadinessForProject.dataSets[2].data += 1;
    }
    if (applicationData?.step1?.section5?.projectFinancialReadiness === '4') {
      widgetsData.financialReadinessForProject.dataSets[3].data += 1;
    }
    if (applicationData?.step1?.section5?.projectFinancialReadiness === '5') {
      widgetsData.financialReadinessForProject.dataSets[4].data += 1;
    }

    // Financial Readiness (General)
    if (applicationData?.step1?.section6?.financialReadiness === '1') {
      widgetsData.financialReadinessGeneral.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section6?.financialReadiness === '2') {
      widgetsData.financialReadinessGeneral.dataSets[1].data += 1;
    }
    if (applicationData?.step1?.section6?.financialReadiness === '3') {
      widgetsData.financialReadinessGeneral.dataSets[2].data += 1;
    }
    if (applicationData?.step1?.section6?.financialReadiness === '4') {
      widgetsData.financialReadinessGeneral.dataSets[3].data += 1;
    }
    if (applicationData?.step1?.section6?.financialReadiness === '5') {
      widgetsData.financialReadinessGeneral.dataSets[4].data += 1;
    }

    // Understand Process
    if (applicationData?.step1?.section5?.understandProcess === '1') {
      widgetsData.understandProcess.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section5?.understandProcess === '2') {
      widgetsData.understandProcess.dataSets[1].data += 1;
    }
    if (applicationData?.step1?.section5?.understandProcess === '3') {
      widgetsData.understandProcess.dataSets[2].data += 1;
    }
    if (applicationData?.step1?.section5?.understandProcess === '4') {
      widgetsData.understandProcess.dataSets[3].data += 1;
    }
    if (applicationData?.step1?.section5?.understandProcess === '5') {
      widgetsData.understandProcess.dataSets[4].data += 1;
    }

    // Community Support
    if (applicationData?.step1?.section6?.communitySupport === '1') {
      widgetsData.communitySupport.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section6?.communitySupport === '2') {
      widgetsData.communitySupport.dataSets[1].data += 1;
    }
    if (applicationData?.step1?.section6?.communitySupport === '3') {
      widgetsData.communitySupport.dataSets[2].data += 1;
    }
    if (applicationData?.step1?.section6?.communitySupport === '4') {
      widgetsData.communitySupport.dataSets[3].data += 1;
    }
    if (applicationData?.step1?.section6?.communitySupport === '5') {
      widgetsData.communitySupport.dataSets[4].data += 1;
    }

    // Have you identified a property?
    if (applicationData?.step2?.q1 === 'yes') {
      widgetsData.isPropertyIdentified.dataSets[0].data += 1;
    }
    if (applicationData?.step2?.q1 === 'no') {
      widgetsData.isPropertyIdentified.dataSets[1].data += 1;
    }

    // Properties Rent/Owned
    if (applicationData?.step1?.section2?.rentStatus.toLowerCase() === 'own') {
      widgetsData.rentOrOwned.dataSets[0].data += 1;
    }
    if (applicationData?.step1?.section2?.rentStatus.toLowerCase() === 'rent') {
      widgetsData.rentOrOwned.dataSets[1].data += 1;
    }
  });

  const _transformDataForPieChart = (data: { [key: string]: number }) => {
    const transformedData = Object.keys(data).map((d) => ({
      type: d,
      count: data[d],
    }));

    return transformProjectTypeChartDataSets(transformedData);
  };

  const convertIntoPercentage = (arrayOfData: { data: number; color: string }[]) => {
    const totalData = arrayOfData.reduce((acc: number, currentData: { data: number; color: string }) => {
      return acc + currentData.data;
    }, 0);
    const percentage = arrayOfData?.map((d: { data: number; color: string }) => {
      const convertNumberIntoPercentage = (d.data * 100) / totalData;
      const convertFloatToInteger = Math.ceil(convertNumberIntoPercentage);
      return {
        ...d,
        data: convertFloatToInteger ? convertFloatToInteger : 0,
      };
    });

    return percentage;
  };
  convertIntoPercentage(widgetsData?.totalLiensOrViolation?.dataSets);

  return (
    <div className='max-w-screen-3xl mx-auto'>
      <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
        <span
          className={`${
            viewStatus === 'ALL' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('ALL')}
        >
          All Applications
        </span>
        <span
          className={`${
            viewStatus === 'APPROVED' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('APPROVED')}
        >
          Approved Applications
        </span>
      </div>
      {viewStatus === 'ALL' && (
        <div>
          <div className='space-y-3'>
            <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
              <TotalApplicationsWidget count={widgetsData?.totalApplications ?? 0} />
            </section>
            <section className='grid md:grid-cols-2 xl:grid-cols-3 gap-4'>
              <Card title='Own additional property'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.ownAdditionalProperty?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.ownAdditionalProperty?.dataSets)}
                      percentage={true}
                    />
                  </div>
                </div>
              </Card>
              <Card title='Tried the process'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.triedProcess?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.triedProcess?.dataSets)}
                      percentage={true}
                    />
                  </div>
                </div>
              </Card>
              <Card title='Total Municipal liens | Code violations'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.totalLiensOrViolation?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.totalLiensOrViolation?.dataSets)}
                      percentage={true}
                    />
                  </div>
                </div>
              </Card>
            </section>
            <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4'>
              <Card title='Have you identified a property?'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.isPropertyIdentified?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.isPropertyIdentified?.dataSets)}
                      percentage={true}
                    />
                  </div>
                </div>
              </Card>
              <Card title='Rent vs Own'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.rentOrOwned?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.rentOrOwned?.dataSets)}
                      percentage={true}
                    />
                  </div>
                </div>
              </Card>
            </section>
            <section className='my-4 grid md:grid-cols-2 xl:grid-cols-3 gap-4'>
              <Card title='Financial Readiness for Project'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.financialReadinessForProject?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.financialReadinessForProject?.dataSets)}
                      percentage={true}
                      display={false}
                    />
                  </div>
                </div>
                <div className='border flex text-gray-500 justify-around py-3'>
                  {convertIntoPercentage(widgetsData?.financialReadinessForProject?.dataSets).map((d: any, index) => {
                    return (
                      <div key={index}>
                        <div className='flex'>
                          <div
                            className={`w-5 h-5 text-xs rounded-full mr-3 ${
                              staticColor[index] === 'yellow'
                                ? 'bg-pieChartColor-yellow'
                                : staticColor[index] === 'blue'
                                  ? 'bg-pieChartColor-blue'
                                  : staticColor[index] === 'gray'
                                    ? 'bg-pieChartColor-gray'
                                    : staticColor[index] === 'navy'
                                      ? 'bg-pieChartColor-navy'
                                      : 'bg-pieChartColor-green'
                            }`}
                          ></div>
                          {widgetsData?.financialReadinessForProject?.dataLabels[index]}{' '}
                        </div>
                        <div className='pl-8'>{`${d?.data}%`}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card title='Financial Readiness (General)'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.financialReadinessGeneral?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.financialReadinessGeneral?.dataSets)}
                      percentage={true}
                      display={false}
                    />
                  </div>
                </div>
                <div className='border flex text-gray-500 justify-around py-3'>
                  {convertIntoPercentage(widgetsData?.financialReadinessGeneral?.dataSets).map((d: any, index) => {
                    return (
                      <div key={index}>
                        <div className='flex'>
                          <div
                            className={`w-5 h-5 text-xs rounded-full mr-3 ${
                              staticColor[index] === 'yellow'
                                ? 'bg-pieChartColor-yellow'
                                : staticColor[index] === 'blue'
                                  ? 'bg-pieChartColor-blue'
                                  : staticColor[index] === 'gray'
                                    ? 'bg-pieChartColor-gray'
                                    : staticColor[index] === 'navy'
                                      ? 'bg-pieChartColor-navy'
                                      : 'bg-pieChartColor-green'
                            }`}
                          ></div>
                          {widgetsData?.financialReadinessGeneral?.dataLabels[index]}{' '}
                        </div>
                        <div className='pl-8'>{`${d?.data}%`}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card title='Understand Process'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.understandProcess?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.understandProcess?.dataSets)}
                      percentage={true}
                      display={false}
                    />
                  </div>
                </div>
                <div className='border flex text-gray-500 justify-around py-3'>
                  {convertIntoPercentage(widgetsData?.understandProcess?.dataSets).map((d: any, index) => {
                    return (
                      <div key={index}>
                        <div className='flex'>
                          <div
                            className={`w-5 h-5 text-xs rounded-full mr-3 ${
                              staticColor[index] === 'yellow'
                                ? 'bg-pieChartColor-yellow'
                                : staticColor[index] === 'blue'
                                  ? 'bg-pieChartColor-blue'
                                  : staticColor[index] === 'gray'
                                    ? 'bg-pieChartColor-gray'
                                    : staticColor[index] === 'navy'
                                      ? 'bg-pieChartColor-navy'
                                      : 'bg-pieChartColor-green'
                            }`}
                          ></div>
                          {widgetsData?.understandProcess?.dataLabels[index]}{' '}
                        </div>
                        <div className='pl-8'>{`${d?.data}%`}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>
              <Card title='Community Support'>
                <div className='p-5 flex flex-col items-center '>
                  <div className='w-5/6 lg:w-2/3 '>
                    <DoughnutChart
                      dataLabels={widgetsData?.communitySupport?.dataLabels}
                      datasets={convertIntoPercentage(widgetsData?.communitySupport?.dataSets)}
                      percentage={true}
                      display={false}
                    />
                  </div>
                </div>
                <div className='border flex text-gray-500 justify-around py-3'>
                  {convertIntoPercentage(widgetsData?.communitySupport?.dataSets).map((d: any, index) => {
                    return (
                      <div key={index}>
                        <div className='flex'>
                          <div
                            className={`w-5 h-5 text-xs rounded-full mr-3 ${
                              staticColor[index] === 'yellow'
                                ? 'bg-pieChartColor-yellow'
                                : staticColor[index] === 'blue'
                                  ? 'bg-pieChartColor-blue'
                                  : staticColor[index] === 'gray'
                                    ? 'bg-pieChartColor-gray'
                                    : staticColor[index] === 'navy'
                                      ? 'bg-pieChartColor-navy'
                                      : 'bg-pieChartColor-green'
                            }`}
                          ></div>
                          {widgetsData?.communitySupport?.dataLabels[index]}{' '}
                        </div>
                        <div className='pl-8'>{`${d?.data}%`}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </section>
          </div>
        </div>
      )}
      {viewStatus === 'APPROVED' && <GStrategiesApprovedDashboard data={data} />}
    </div>
  );
};
